"use client";

import { MediaCitation } from "~/modules/media_citations/types";
import { Anchor } from "~/components/Anchor";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowUpRight,
    faArrowRight,
} from "@fortawesome/pro-regular-svg-icons";
import { Link } from "~/components/Link";
import { Routes } from "~/routes";
import { BillingUserEvents } from "~/modules/analytics/events/billing";
import { cn } from "~/lib/utils";

// // // //

interface CitationsCarouselProps {
    citations: MediaCitation[];
    className?: string;
}

function CitationCard(props: { citation: MediaCitation }) {
    const { citation } = props;

    return (
        <Anchor
            href={citation.url}
            analyticsLabel={`Pricing Citations - ${citation.title}`}
            className="shrink-0 w-72 rounded-lg overflow-hidden bg-card border hover:bg-blue-50 transition-all hover:shadow-md"
            openInNewTab
        >
            <div className="p-3 flex flex-col gap-2 h-full">
                <div className="flex items-center gap-2">
                    <ImageLoader
                        src={citation.publisher.icon_url || RWA_COIN_ICON}
                        alt={`${citation.publisher.name} icon`}
                        className="w-4 h-4 object-contain rounded-sm shrink-0"
                    />
                    <p className="font-medium text-xs text-muted-foreground">
                        {citation.publisher.name}
                    </p>
                </div>

                <h3 className="text-sm font-semibold text-foreground line-clamp-2 flex-1">
                    {citation.title}
                </h3>

                <p className="flex items-center text-xs text-primary font-medium">
                    Read Article
                    <FontAwesomeIcon
                        icon={faArrowUpRight}
                        className="w-2.5 h-2.5 ml-1"
                    />
                </p>
            </div>
        </Anchor>
    );
}

export function CitationsCarousel(props: CitationsCarouselProps) {
    const { citations = [], className } = props;

    // Don't render if no citations
    if (citations.length === 0) {
        return null;
    }

    // Double the array for seamless infinite scroll
    const duplicatedCitations = [...citations, ...citations];

    return (
        <div className={cn("mt-20 select-none", className)}>
            <div className="flex items-center justify-center gap-3 mb-8">
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                    Used by leading institutions
                </p>
                <Link
                    href={Routes.citations}
                    analyticsLabel={
                        BillingUserEvents.CitationsCarouselViewAllLinkClick
                    }
                    className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                >
                    View all usage
                    <FontAwesomeIcon icon={faArrowRight} className="w-3 h-3" />
                </Link>
            </div>

            {/* Carousel Container */}
            <div className="relative overflow-hidden">
                {/* Gradient Masks */}
                <div className="absolute left-0 top-0 bottom-0 w-24 bg-linear-to-r from-background to-transparent z-10" />
                <div className="absolute right-0 top-0 bottom-0 w-24 bg-linear-to-l from-background to-transparent z-10" />

                {/* Scrolling Track */}
                <div className="flex gap-4 animate-scroll-logos">
                    {duplicatedCitations.map((citation, index) => (
                        <CitationCard
                            key={`${citation.id}-${index}`}
                            citation={citation}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
